import { gql } from '@apollo/client';

export const EXPIRED_MILESTONES = gql`
  query expiredMilestones($input: PlanningCrewMemberInput) {
    expiredMilestones(input: $input) {
      id
      milestones {
        id
        name
        hours
        isExpired
        plannedStartDate
        currentStatus {
          id
          codeI18n
        }
        commodity {
          materialCommodity {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              tag
            }
          }
          weld {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              tag
            }
          }
        }
      }
    }
  }
`;
export const EXPIRED_MILESTONES_COUNT = gql`
  query expiredMilestoneCount($input: PlanningCrewMemberInput) {
    expiredMilestoneCount(input: $input) {
      id
      numExpiredMilestones
    }
  }
`;

const fragments = {
  planningWeeks: gql`
    fragment PlanningWeeks on PlanningWeekStats {
      id
      weekNumber
      year
      startDate
      endDate
    }
  `,
};

export const GET_PLANNING_WEEKS = gql`
  query planningWeekStats($input: PlanningFilterInput) {
    planningWeekStats(input: $input) {
      ...PlanningWeeks
    }
  }
  ${fragments.planningWeeks}
`;

export const GET_PLANNING_WEEK_DATA = gql`
  query planningWeekStats($input: PlanningFilterInput) {
    planningWeekStats(input: $input) {
      ...PlanningWeeks
      committedMilestoneStats {
        id
        numMilestones
        quantity {
          quantity
          unitOfMeasure
        }
        hours
      }
      claimedMilestoneStats {
        id
        numMilestones
        quantity {
          quantity
          unitOfMeasure
        }
        hours
      }
      plannedMilestoneStats {
        id
        numMilestones
        quantity {
          quantity
          unitOfMeasure
        }
        hours
      }
      target {
        id
        hours
      }
      crewMember {
        id
        firstName
        lastName
        persona
      }
    }
  }
  ${fragments.planningWeeks}
`;
export const GET_PLANNED_MILESTONES = gql`
  query plannedMilestones($input: PlanningFilterInput) {
    plannedMilestones(input: $input) @connection(key: "plannedMilestones", filter: ["input"]) {
      id
      weekNumber
      year
      startDate
      endDate
      milestones {
        id
        name
        isExpired
        hours
        plannedStartDate
        commodity {
          weld {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              secondaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              otherOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
            }
            type {
              id
              name
            }
          }
          materialCommodity {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              secondaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              otherOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
            }
            type {
              id
              name
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mwrNumber
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
        }
        currentStatus {
          id
          codeI18n
          createdAt
        }
        quantity {
          quantity
          unitOfMeasure
        }
      }
    }
  }
`;

export const GET_PLANNING_BACKLOG_MILESTONE_LIST = gql`
  query backlogMilestones($input: PlanningBacklogMilestonesInput) {
    backlogMilestones(input: $input) @connection(key: "backlogMilestones", filter: ["input"]) {
      id
      milestones {
        id
        currentStatus {
          id
          codeI18n
          createdAt
        }
        hours
        name
        plannedStartDate
        isExpired
        commodity {
          weld {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              secondaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              otherOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
            }
            type {
              id
              name
            }
          }
          materialCommodity {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              secondaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              otherOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
            }
            type {
              id
              name
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mwrNumber
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
        }
        quantity {
          quantity
          unitOfMeasure
        }
      }
    }
  }
`;

export const SAVE_PLAN = gql`
  mutation ($input: SavePlanningInput!) {
    savePlanning(input: $input) {
      commodityMilestones {
        id
        weekNumber
        plannedEndDate
        plannedStartDate
        isExpired
        commodity {
          materialCommodity {
            id
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
          }
          weld {
            id
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
          }
        }
        currentStatus {
          id
          codeI18n
          createdAt
        }
      }
      error {
        errorCode
        commodityMilestones {
          id
          name
          weekNumber
          plannedEndDate
          plannedStartDate
          currentStatus {
            id
            codeI18n
            createdAt
          }
          commodity {
            weld {
              id
              tag
              type {
                id
                name
              }
            }
            materialCommodity {
              id
              tag
              type {
                id
                name
              }
            }
          }
        }
      }
      planningStats {
        crewMember {
          id
          firstName
          lastName
          persona
        }
        planningWeekStats {
          id
          weekNumber
          year
          startDate
          endDate
          committedMilestoneStats {
            id
            numMilestones
            quantity {
              quantity
              unitOfMeasure
            }
            hours
          }
          claimedMilestoneStats {
            id
            numMilestones
            quantity {
              quantity
              unitOfMeasure
            }
            hours
          }
          plannedMilestoneStats {
            id
            numMilestones
            quantity {
              quantity
              unitOfMeasure
            }
            hours
          }
          target {
            id
            hours
          }
          crewMember {
            id
            firstName
            lastName
            persona
          }
        }
      }
      expiredMilestoneCount {
        id
        numExpiredMilestones
      }
    }
  }
`;

export const UNPLAN = gql`
  mutation ($input: UnplanInput!) {
    unplan(input: $input) {
      commodityMilestones {
        id
        weekNumber
        plannedEndDate
        plannedStartDate
        isExpired
        hours
        name
        currentStatus {
          id
          codeI18n
          createdAt
        }
        commodity {
          weld {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              primaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              secondaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              otherOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
            }
          }
          materialCommodity {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              primaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              secondaryOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
              otherOwners {
                persona
                crewMember {
                  id
                  persona
                }
              }
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
        }
        quantity {
          quantity
          unitOfMeasure
        }
      }
      error {
        errorCode
        commodityMilestones {
          id
          weekNumber
          plannedEndDate
          plannedStartDate
          currentStatus {
            codeI18n
          }
        }
      }
      planningStats {
        crewMember {
          id
          firstName
          lastName
          persona
        }
        planningWeekStats {
          id
          weekNumber
          year
          startDate
          endDate
          committedMilestoneStats {
            id
            numMilestones
            quantity {
              quantity
              unitOfMeasure
            }
            hours
          }
          claimedMilestoneStats {
            id
            numMilestones
            quantity {
              quantity
              unitOfMeasure
            }
            hours
          }
          plannedMilestoneStats {
            id
            numMilestones
            quantity {
              quantity
              unitOfMeasure
            }
            hours
          }
          target {
            id
            hours
          }
          crewMember {
            id
            firstName
            lastName
            persona
          }
        }
      }
      expiredMilestoneCount {
        id
        numExpiredMilestones
      }
    }
  }
`;

export const GET_WEEKLY_MILESTONE_PROGRESS = gql`
  query weeklyMilestoneProgress($input: WeeklyMilestoneProgressInput!) {
    weeklyMilestoneProgress(input: $input) {
      weeklyMilestoneAnalysis {
        targetHours
        cumulativeMilestoneHours
      }
      milestoneProgress {
        id
        date
        weekDay
        targetHours
        milestonehours
        milestoneApprovedHours
        cumulativeMilestoneHours
      }
    }
  }
`;

export const GET_THREE_WEEK_LOOK_AHEAD_DATA = gql`
  query weeklyLookAheadStatsByPersona($input: CrewMemberWeeklyLookAheadStatsInput!) {
    weeklyLookAheadStatsByPersona(input: $input) {
      crewMember {
        id
        firstName
        lastName
        persona
        imageUrl
        persona
      }
      weeklyLookAheadStats {
        id
        weekNumber
        year
        startDate
        endDate
        approvedMilestoneStats {
          numMilestones
          hours
          quantity {
            quantity
            unitOfMeasure
          }
        }
        claimedMilestoneStats {
          numMilestones
          hours
          quantity {
            quantity
            unitOfMeasure
          }
        }
        plannedMilestoneStats {
          numMilestones
          hours
          quantity {
            quantity
            unitOfMeasure
          }
        }
        target {
          numMilestones
          hours
          quantity {
            quantity
            unitOfMeasure
          }
        }
        earningsInPlan
        planningInTarget
      }
    }
  }
`;
